import { ArrowForward, Check } from '@mui/icons-material'
import { Box, Card, CardContent, Chip, Grid, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ActionButton } from '../action-buttons'
import { useStyles } from './styles'

/**
 * Status indicator component for feature items
 * Renders different visual states: pending (empty circle), current (circle with dot), completed (checkmark)
 */
const FeatureStatusIndicator = ({ status, classes }) => {
  if (status === 'completed') {
    return (
      <Box className={`${classes.statusIndicator} ${classes.statusCompleted}`}>
        <Check className={classes.statusCheckIcon} />
      </Box>
    )
  }

  if (status === 'current') {
    return (
      <Box className={`${classes.statusIndicator} ${classes.statusCurrent}`}>
        <Box className={classes.statusCurrentDot} />
      </Box>
    )
  }

  // Default: pending (empty circle)
  return (
    <Box className={`${classes.statusIndicator} ${classes.statusPending}`} />
  )
}

const FeaturePromoBanner = ({
  badgeText,
  badgeVariant = 'primary', // 'primary' or 'dark'
  programIcon,
  title,
  subtitle,
  description,
  buttonLabel,
  buttonLink,
  buttonOnClick,
  buttonLoading = false,
  buttonLoadingIndicator,
  features = [],
  useBrandBorder = false,
  variant = 'gradient', // 'gradient' or 'solid'
  dataTest = 'feature-promo-banner'
}) => {
  const classes = useStyles({ useBrandBorder, badgeVariant, variant })
  const navigate = useNavigate()

  const handleButtonClick = () => {
    if (buttonOnClick) {
      buttonOnClick()
    } else if (buttonLink) {
      navigate(buttonLink)
    }
  }

  const hasFeatures = features.length > 0

  return (
    <Card className={classes.root} elevation={0} data-test={dataTest}>
      <CardContent className={classes.content}>
        <Grid container spacing={4} className={classes.container} alignItems='center'>
          {/* Left Section - 7 columns if features exist, 8 columns if no features */}
          <Grid item xs={12} md={hasFeatures ? 7 : 8} className={classes.leftSection}>
            {badgeText && (
              <Chip
                label={badgeText}
                className={classes.badge}
                data-test={`${dataTest}-badge`}
              />
            )}
            {(programIcon || title || subtitle) && (
              <Box className={classes.titleContainer}>
                {programIcon && (
                  <Box className={classes.programIconContainer}>
                    {React.createElement(programIcon, { className: classes.programIcon })}
                  </Box>
                )}
                <Box className={classes.titleWrapper}>
                  {title && (
                    <Typography className={classes.title} variant='h2' data-test={`${dataTest}-title`}>
                      {title}
                    </Typography>
                  )}
                  {subtitle && (
                    <Typography className={classes.subtitle} data-test={`${dataTest}-subtitle`}>
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {description && (
              <Typography className={classes.description} data-test={`${dataTest}-description`}>
                {description}
              </Typography>
            )}
            {/* Only show button below copy if features exist */}
            {buttonLabel && hasFeatures && (
              <Box className={classes.buttonContainer}>
                <ActionButton
                  variant='primary'
                  label={buttonLabel}
                  endIcon={!buttonLoading ? <ArrowForward /> : null}
                  onClick={handleButtonClick}
                  loading={buttonLoading}
                  loadingIndicator={buttonLoadingIndicator}
                  data-test={`${dataTest}-cta`}
                />
              </Box>
            )}
          </Grid>

          {/* Empty column offset - 1 column (only if features exist) */}
          {hasFeatures && <Grid item xs={0} md={1} />}

          {/* Right Section - Features or Button */}
          {hasFeatures
            ? (
              <Grid item xs={12} md={4} className={classes.rightSection}>
                {features.map((feature, index) => {
                  const IconComponent = feature.icon
                  const hasStatus = feature.status !== undefined
                  return (
                    <Box key={index} className={classes.featureItem} data-test={`${dataTest}-feature-${index}`}>
                      {hasStatus
                        ? (
                          <FeatureStatusIndicator status={feature.status} classes={classes} />
                          )
                        : (
                          <Box className={classes.featureIcon}>
                            {IconComponent && <IconComponent />}
                          </Box>
                          )}
                      <Box className={classes.featureText}>
                        <Typography className={classes.featureTitle}>
                          {feature.title}
                        </Typography>
                        <Typography className={classes.featureDescription}>
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
              </Grid>
              )
            : (
              // Show button on the right when no features
                buttonLabel && (
                  <Grid item xs={12} md={4} className={classes.rightButtonSection}>
                    <ActionButton
                      variant='primary'
                      label={buttonLabel}
                      endIcon={!buttonLoading ? <ArrowForward /> : null}
                      onClick={handleButtonClick}
                      loading={buttonLoading}
                      loadingIndicator={buttonLoadingIndicator}
                      data-test={`${dataTest}-cta`}
                    />
                  </Grid>
                )
              )}
        </Grid>
      </CardContent>
    </Card>
  )
}

FeaturePromoBanner.propTypes = {
  badgeText: PropTypes.string,
  badgeVariant: PropTypes.oneOf(['primary', 'dark']),
  programIcon: PropTypes.elementType,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]),
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  buttonOnClick: PropTypes.func,
  buttonLoading: PropTypes.bool,
  buttonLoadingIndicator: PropTypes.string,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.elementType,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      status: PropTypes.oneOf(['pending', 'current', 'completed'])
    })
  ),
  useBrandBorder: PropTypes.bool,
  variant: PropTypes.oneOf(['gradient', 'solid']),
  dataTest: PropTypes.string
}

export default FeaturePromoBanner
